import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Droplet, Disc3, Cog, Gauge, BatteryCharging, ScanLine, ArrowRight, ShieldCheck, Clock, BadgeCheck, Phone } from "lucide-react";
import AIConciergeWidget from "@/components/AIConciergeWidget";
import DemoTopNav from "@/components/demos/DemoTopNav";
import DemoBookingModal from "@/components/demos/DemoBookingModal";
import { DEMO_THEMES } from "@/data/demoThemes";

const RED = "#C0242E";
const NAVY = "#14161A";
const PANEL = "#1C1F26";
const body = { fontFamily: "'Plus Jakarta Sans', sans-serif" };
const display = { fontFamily: "'Plus Jakarta Sans', sans-serif", fontWeight: 800 };
const IMG = "https://images.unsplash.com/photo-1619642751034-765dfdf7c58e?crop=entropy&cs=srgb&fm=jpg&w=1000&q=80&ixlib=rb-4.1.0";
const SERVICES = [
  { icon: Droplet, t: "Oil Change", p: "from $39" },
  { icon: Disc3, t: "Brake Service", p: "from $149" },
  { icon: Cog, t: "Engine Repair", p: "quoted" },
  { icon: Gauge, t: "Tires & Alignment", p: "from $89" },
  { icon: BatteryCharging, t: "Battery Service", p: "from $129" },
  { icon: ScanLine, t: "Diagnostics", p: "$89" },
];
const TRUST = [{ icon: BadgeCheck, t: "ASE-Certified Technicians" }, { icon: ShieldCheck, t: "12-Month / 12k-Mile Warranty" }, { icon: Clock, t: "Same-Day Service" }];
const YEARS = ["2024+", "2020–2023", "2015–2019", "2010–2014", "Older than 2010"];

export default function PrecisionAutoDemo() {
  const [book, setBook] = useState(false);
  const [preService, setPreService] = useState(null);
  useEffect(() => { window.scrollTo(0, 0); document.title = "Precision Auto Care (Demo) | WebsiteTech.pro"; }, []);
  const selects = [
    { key: "service", label: "Service Needed", options: SERVICES.map((s) => s.t) },
    { key: "vehicle", label: "Vehicle Year", options: YEARS },
  ];

  return (
    <div className="min-h-screen" style={{ background: NAVY, color: "#F4F4F5", ...body }} data-testid="auto-demo">
      <Link to="/demos" className="block w-full text-center text-[11px] tracking-widest uppercase py-1.5 text-white" style={{ background: RED }}>WebsiteTech.pro Demo · Auto Repair — view all demos</Link>
      <DemoTopNav testid="auto-nav"
        logo={<div className="leading-tight"><div className="text-lg font-extrabold tracking-tight text-white">PRECISION<span style={{ color: RED }}>AUTO</span></div><div className="text-[8px] tracking-[0.3em] uppercase text-white/50">Care You Can Trust</div></div>}
        items={["Services", "Why Us", "Reviews", "Specials", "Contact"]} ctaLabel="Book an Appointment" onCta={() => setBook(true)}
        theme={{ bg: "rgba(20,22,26,0.92)", text: "#F4F4F5", accent: RED, accentText: "#fff", border: "#2a2e37" }} />

      <header data-testid="auto-hero" className="relative overflow-hidden">
        <img src={IMG} alt="" className="absolute inset-y-0 right-0 h-full w-1/2 object-cover opacity-40 hidden md:block" />
        <div className="absolute inset-0 hidden md:block" style={{ background: "linear-gradient(90deg, #14161A 50%, rgba(20,22,26,0.5))" }} />
        <div className="relative max-w-7xl mx-auto px-6 py-20">
          <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }} className="max-w-xl">
            <span className="text-xs tracking-[0.3em] uppercase" style={{ color: RED }}>Trusted Since 1998</span>
            <h1 className="mt-4 text-4xl sm:text-5xl lg:text-6xl leading-[1.02]" style={display}>Your car.<br />Our expertise.</h1>
            <p className="mt-5 text-lg text-white/70 max-w-md">Honest, expert auto service that keeps you moving — done right the first time.</p>
            <div className="mt-7 flex flex-wrap gap-3">
              <button onClick={() => setBook(true)} data-testid="auto-book-hero" className="rounded-lg px-7 py-3.5 text-xs tracking-[0.18em] uppercase font-bold text-white flex items-center gap-2" style={{ background: RED }}>Book an Appointment <ArrowRight size={14} /></button>
              <span className="rounded-lg px-5 py-3.5 text-xs tracking-[0.15em] uppercase font-bold flex items-center gap-2" style={{ background: "#F4C430", color: NAVY }}>Service Special · $25 Off First Visit</span>
            </div>
          </motion.div>
        </div>
        {/* booking bar */}
        <div className="max-w-5xl mx-auto px-6 pb-8 relative z-10">
          <div className="rounded-xl border p-4 flex flex-col md:flex-row gap-3 items-stretch md:items-center" style={{ background: PANEL, borderColor: "#2a2e37" }} data-testid="auto-booking-bar">
            <div className="flex-1 grid grid-cols-2 md:grid-cols-3 gap-3 text-sm">
              <div><div className="text-[10px] uppercase tracking-widest text-white/40">Service</div><div className="font-medium">Oil Change</div></div>
              <div><div className="text-[10px] uppercase tracking-widest text-white/40">Vehicle</div><div className="font-medium">Select year</div></div>
              <div><div className="text-[10px] uppercase tracking-widest text-white/40">Date</div><div className="font-medium">Choose a date</div></div>
            </div>
            <button onClick={() => setBook(true)} data-testid="auto-check-availability" className="rounded-lg px-8 py-3.5 text-xs tracking-[0.18em] uppercase font-bold text-white whitespace-nowrap" style={{ background: RED }}>Check Availability</button>
          </div>
        </div>
      </header>

      <section data-testid="auto-services" className="max-w-7xl mx-auto px-6 py-16">
        <div className="mb-10"><span className="text-xs tracking-[0.25em] uppercase font-bold" style={{ color: RED }}>Our Services</span><h2 className="mt-2 text-3xl font-extrabold">Complete care, one shop</h2></div>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
          {SERVICES.map((s, i) => (
            <button key={s.t} onClick={() => { setPreService(s.t); setBook(true); }} className="group text-left rounded-xl p-6 border hover:border-white/30 transition-colors flex items-center gap-4" style={{ background: PANEL, borderColor: "#2a2e37" }} data-testid={`auto-service-${i}`}>
              <div className="h-11 w-11 rounded-lg grid place-items-center shrink-0" style={{ background: `${RED}22`, color: RED }}><s.icon size={20} /></div>
              <div className="flex-1"><div className="font-bold">{s.t}</div><div className="text-xs text-white/50 uppercase tracking-wide">{s.p}</div></div>
              <ArrowRight size={15} className="opacity-30 group-hover:opacity-100 transition-opacity" />
            </button>
          ))}
        </div>
      </section>

      <section className="border-y" style={{ background: PANEL, borderColor: "#2a2e37" }}>
        <div className="max-w-7xl mx-auto px-6 py-10 grid grid-cols-1 md:grid-cols-3 gap-6">
          {TRUST.map((t) => (
            <div key={t.t} className="flex items-center gap-3 justify-center"><t.icon size={24} style={{ color: RED }} /><span className="font-semibold">{t.t}</span></div>
          ))}
        </div>
      </section>

      <section className="max-w-4xl mx-auto px-6 py-16 text-center">
        <p className="text-2xl md:text-3xl leading-relaxed font-semibold">"Fair prices, no upselling, and my car was ready the same day. Precision Auto has earned a customer for life."</p>
        <div className="mt-4 text-sm text-white/50">— Marcus D., ★★★★★</div>
      </section>

      <footer data-testid="auto-footer" style={{ background: "#0F1115" }}>
        <div className="max-w-7xl mx-auto px-6 py-10 flex flex-wrap items-center justify-between gap-4 text-sm text-white/70">
          <div className="font-extrabold tracking-tight text-white">PRECISION<span style={{ color: RED }}>AUTO</span></div>
          <span className="flex items-center gap-1.5"><Phone size={13} /> (555) 812-4400 · Mon–Sat 7:30 AM – 6:00 PM</span>
          <p className="italic">Care you can trust.</p>
        </div>
      </footer>

      {book && <DemoBookingModal demo="auto" title="Book a service" accent={RED} submitLabel="Request service" selects={selects} prefill={preService ? { service: preService } : {}} onClose={() => { setBook(false); setPreService(null); }} />}
      <AIConciergeWidget demo="auto" concierge={DEMO_THEMES.auto.concierge} />
    </div>
  );
}
